<template>
  <b-modal
    id="request-qr-modal"
    title="Yêu cầu tạo QR"
    size="md"
    centered
    @ok="handleSubmit"
    @hidden="resetForm"
    ok-title="Xác nhận"
    cancel-title="Hủy"
    :ok-disabled="isSubmitting"
  >
    <b-form @submit.prevent="handleSubmit">
      <b-form-group label="Ngân hàng" label-for="bankCode">
        <SearchStaffAutocomplete
          v-model="formData.bankCode"
          id="bankCode"
          placeholder="Chọn ngân hàng"
          :required="true"
          :list="listBankCode"
        />
      </b-form-group>

      <b-form-group label="Mã lô" label-for="lotIn">
        <b-input-group>
          <b-form-input
            id="lotIn"
            v-model="formData.lotIn"
            type="text"
            placeholder="Nhập mã lô"
            :required="true"
          ></b-form-input>
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="generateLotIn">
              <i class="bx bx-revision"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Số lượng" label-for="quantity">
        <b-form-input
          id="quantity"
          v-model.number="formData.quantity"
          type="number"
          min="1"
          max="1000"
          placeholder="Nhập số lượng"
          :required="true"
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Vùng" label-for="region">
        <SearchStaffAutocomplete
          v-model="formData.region"
          id="bankCode"
          placeholder="Chọn vùng"
          :required="true"
          :list="dataConfigRegion"
        />
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="print" v-model="formData.print" switch> In QR code </b-form-checkbox>
      </b-form-group>

      <b-form-group v-if="formData.print" label="Kích thước in:" label-for="qrPrintSize">
        <b-form-select
          id="qrPrintSize"
          v-model="formData.qrPrintSize"
          :options="qrSizeOptions"
          placeholder="Chọn kích thước"
          :required="formData.print"
        ></b-form-select>
      </b-form-group>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="outline-secondary" @click="cancel"> Hủy </b-button>
      <b-button variant="primary" @click="ok" :disabled="isSubmitting || !isFormValid">
        {{ isSubmitting ? 'Đang xử lý...' : 'Xác nhận' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
  import { showMess } from '@/config/utils'
  import { RequestCreateQrVa } from '@/services/customer-support/account'
  import { getBrowserName } from '@/utils/util'
  import SearchStaffAutocomplete from '@/components/cskh/SearchStaffAutocomplete.vue'

  export default {
    name: 'RequestQrModal',
    components: {
      SearchStaffAutocomplete,
    },
    props: {
      // Pass existing data from parent component
      dataConfigRegion: {
        type: Array,
        default: () => [],
      },
      listBankCode: {
        type: Array,
        default: () => [],
      },
      listPrintQr: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        formData: {
          bankCode: '',
          lotIn: '',
          quantity: 1,
          region: '',
          print: false,
          qrPrintSize: '',
          userData: JSON.parse(localStorage.getItem('userData')),
        },
        isSubmitting: false,
        lastRandomNumber: null,
      }
    },
    computed: {
      qrSizeOptions() {
        return this.listPrintQr.map(item => ({
          value: item.id,
          text: item.text,
        }))
      },
      isFormValid() {
        const baseValidation =
          this.formData.bankCode &&
          this.formData.lotIn &&
          this.formData.quantity > 0 &&
          this.formData.region

        if (this.formData.print) {
          return baseValidation && this.formData.qrPrintSize
        }

        return baseValidation
      },

      companyCode() {
        return this.userData?.companyCode || ''
      },
    },
    mounted() {
      this.generateLotIn()
    },
    methods: {
      formatDate(date) {
        const d = date || new Date()
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${year}${month}${day}`
      },
      generateRandomNumber() {
        // Generate a random number between 0 and 999
        let randomNum
        do {
          randomNum = Math.floor(Math.random() * 1000)
        } while (randomNum === this.lastRandomNumber)

        this.lastRandomNumber = randomNum
        // Format to ensure it's always 3 digits (e.g., 001, 042, 999)
        return String(randomNum).padStart(3, '0')
      },
      generateLotIn() {
        const formattedDate = this.formatDate(new Date())
        const randomPart = this.generateRandomNumber()
        this.formData.lotIn = `LO_${formattedDate}_${randomPart}_${this.companyCode}`
      },
      resetForm() {
        this.formData = {
          bankCode: '',
          lotIn: '',
          quantity: 1,
          region: '',
          print: false,
          qrPrintSize: 'K_65_62',
        }
        this.isSubmitting = false
        // Generate new lotIn after reset
        this.generateLotIn()
      },
      async handleSubmit(bvModalEvent) {
        // Prevent modal from closing
        bvModalEvent.preventDefault()

        if (!this.isFormValid) {
          showMess(
            this,
            'Thông báo',
            'Vui lòng điền đầy đủ thông tin',
            'warning',
            'b-toaster-top-right'
          )
          return
        }

        try {
          this.isSubmitting = true

          const payload = {
            bankCode: this.formData.bankCode,
            lotIn: this.formData.lotIn,
            quantity: this.formData.quantity,
            region: this.formData.region,
          }

          if (this.formData.print) {
            payload.print = true
            payload.qrPrintSize = this.formData.qrPrintSize
          }

          const response = await RequestCreateQrVa({
            request: payload,
            responseType: this.formData.print || false,
          })

          if (response) {
            if (response.code !== 1000 && !this.formData.print) {
              showMess(
                this,
                'Thất bại',
                response.message || 'Có lỗi xảy ra khi tạo yêu cầu QR',
                'danger',
                'b-toaster-top-right'
              )
            } else {
              showMess(
                this,
                'Thành công',
                'Yêu cầu tạo QR đã được gửi thành công',
                'success',
                'b-toaster-top-right'
              )
              if (this.formData.print) {
                this.downloadQRPdf(response)
              }
            }

            this.$emit('request-successful')
            this.$bvModal.hide('request-qr-modal')
          } else {
            showMess(
              this,
              'Lỗi',
              response.message || 'Có lỗi xảy ra khi tạo yêu cầu QR',
              'danger',
              'b-toaster-top-right'
            )
          }
        } catch (error) {
          showMess(
            this,
            'Lỗi',
            error.message || 'Có lỗi xảy ra khi tạo yêu cầu QR',
            'danger',
            'b-toaster-top-right'
          )
        } finally {
          this.isSubmitting = false
        }
      },
      show() {
        this.$bvModal.show('request-qr-modal')
        this.formData.qrPrintSize = this.qrSizeOptions[0].value || 'K_65_62'
      },
      downloadQRPdf(res) {
        res &&
          (() => {
            const href = URL.createObjectURL(res)

            switch (getBrowserName()) {
              case 'Chrome':
                ;(() => {
                  // iframe
                  const iframe = document.createElement('iframe')
                  iframe.style.display = 'none'
                  iframe.src = href
                  document.body.appendChild(iframe)
                  iframe.contentWindow.focus()
                  iframe.contentWindow.onload = () => {
                    iframe.contentWindow.print()
                  }
                })()
                break
              case 'Firefox':
                ;(() => {
                  // download
                  const link = document.createElement('a')
                  link.href = href
                  link.setAttribute('download', 'qr-code-print.pdf')
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                  URL.revokeObjectURL(href)
                })()
                break
              case 'Safari':
                ;(() => {
                  // new tab
                  const newWindow = window.open(href, '_blank')
                  newWindow &&
                    (() => {
                      newWindow.onload = () => {
                        newWindow.print()
                      }
                    })()
                })()
                break
              default:
                ;(() => {
                  // default new tab
                  const newWindow = window.open(href, '_blank')
                  newWindow &&
                    (() => {
                      newWindow.onload = () => {
                        newWindow.print()
                      }
                    })()
                })()
            }
          })()
      },
    },
  }
</script>
<style scoped lang="scss">
  :deep(.form-control-sm) {
    height: calc(1.8em + 0.5rem + 2px) !important;
  }
</style>
